<template>
  <v-row>
    <v-col cols="12">
      <h1 v-if="!id" class="headline">New Open Group</h1>
      <h1 v-else class="headline">{{ editLabel }}</h1>

      <v-stepper v-model="step" non-linear>
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1" editable
            >Details</v-stepper-step
          >
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2" :editable="isValid"
            >Services</v-stepper-step
          >
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 3" step="3" :editable="isValid"
            >Departure Dates</v-stepper-step
          >
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-row>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="name"
                  label="Name"
                  :outlined="outlinedInputs"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <brand-list-select
                  v-model="brand"
                  :outlined="outlinedInputs"
                ></brand-list-select>
              </v-col>
              <v-col cols="12" sm="3">
                <destination-list-select
                  :destination="destination"
                  :set-default="false"
                  :outlined="outlinedInputs"
                  @onSelect="onDestinationSelect($event)"
                ></destination-list-select>
              </v-col>
              <v-col cols="12" sm="2">
                <department-list
                  v-model="department"
                  :outlined="outlinedInputs"
                ></department-list>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model.number="duration"
                  label="Duration (Days)"
                  type="number"
                  min="1"
                  :outlined="outlinedInputs"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model.number="adults"
                  label="Adults"
                  type="number"
                  min="0"
                  :outlined="outlinedInputs"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model.number="children"
                  label="Children"
                  type="number"
                  min="0"
                  :outlined="outlinedInputs"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <agent-list-select
                  v-model="agentId"
                  :outlined="outlinedInputs"
                ></agent-list-select>
              </v-col>
              <v-col cols="12" sm="5">
                <dates-range-picker
                  :gt-date-from-string="validFrom"
                  :gt-date-to-string="validTo"
                  custom-date-from-label="Valid From"
                  custom-date-to-label="Valid To"
                  :outlined="outlinedInputs"
                  :clearable="true"
                  @onRangeSelected="onRangeSelected($event)"
                  @onClear="
                    validFrom = null;
                    validTo = null;
                  "
                  hide-details
                ></dates-range-picker>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <gt-multi-select
                  v-model="departureDays"
                  :items="dayOptions"
                  item-text="name"
                  item-value="value"
                  label="Days of Week"
                  :outlined="outlinedInputs"
                  :select-all="true"
                ></gt-multi-select>
              </v-col>
            </v-row>
            <!-- #11180 — defaults inherited by every departure date of this group -->
            <v-row>
              <v-col cols="12" sm="3">
                <service-status-list-select
                  :value="serviceStatusId"
                  :service-status-id="serviceStatusId"
                  :is-disabled="false"
                  :outlined="outlinedInputs"
                  @input="serviceStatusId = $event"
                ></service-status-list-select>
              </v-col>
              <v-col cols="12" sm="3">
                <itinerary-status-list-select
                  v-model="internalStatus"
                  label="Internal Status"
                  :skip-autoselect="true"
                ></itinerary-status-list-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="description"
                  label="Short Description"
                  rows="2"
                  :outlined="outlinedInputs"
                ></v-textarea>
              </v-col>
            </v-row>
            <v-btn color="primary" text :disabled="!isValid" @click="step = 2">
              Continue To Services
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-stepper-content>

          <v-stepper-content step="2" class="py-0">
            <v-container fluid py-0>
              <v-row justify="end">
                <v-switch
                  label="Drag &amp; Drop"
                  v-model="dayByDay"
                  dense
                  hide-details
                  @change="onDayByDayViewChange"
                ></v-switch>
              </v-row>
            </v-container>
            <open-group-builder-day-by-day
              v-if="dayByDay"
              :destination-id="destination ? destination.id : ''"
              :duration="duration || 7"
              :items="items"
              @update-item="onUpdateItem"
              @add-item="onAddItem"
              @remove-item="onRemoveItem"
              @supplier-edit="onSupplierEdit"
              @onItemRemarks="onOpenItemRemarks"
              @onExtraRemove="onExtraRemoveDnd"
            ></open-group-builder-day-by-day>
            <template v-else>
              <open-group-item-quick-add
                :destination-id="destination ? destination.id : null"
                :group-duration="duration || 7"
                :group-adults="adults"
                :group-children="children"
                @onAddItem="onAddItem"
              ></open-group-item-quick-add>
              <open-group-items-list
                :items="items"
                :duration="duration || 7"
                @remove-item="onRemoveItem"
                @supplier-edit="onSupplierEdit"
                @onItemRemarks="onOpenItemRemarks"
              ></open-group-items-list>
            </template>
            <v-btn color="primary" text @click="step = 3">
              Continue To Departure Dates
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-stepper-content>

          <v-stepper-content step="3">
            <open-group-departures-tab
              :open-group-id="id"
              :master-name="name"
              :master-adults="Number(adults) || 0"
              :master-children="Number(children) || 0"
              :master-duration="Number(duration) || 7"
              :master-valid-from="validFrom"
              :master-valid-to="validTo"
              :master-days-of-week="departureDays"
            />
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-row justify="center" class="pa-3">
        <v-btn color="primary" :disabled="!isValid" @click="onSave">{{
          saveLabel
        }}</v-btn>
      </v-row>
    </v-col>
    <modal-supplier-edit
      v-model="editSupplier"
      :show="showSupplierEditor"
      @close="showSupplierEditor = false"
    ></modal-supplier-edit>
    <remarks-edit-dialog
      :show="remarksDialog"
      :value="remarksValue"
      @save="onSaveItemRemarks"
      @close="remarksDialog = false"
    ></remarks-edit-dialog>
    <v-row v-if="debug">
      <v-col cols="12">
        <code>
          {{ agentId }}
        </code>
      </v-col>
    </v-row>
  </v-row>
</template>

<script>
import gql from "graphql-tag";
import DestinationListSelect from "../shared/DestinationListSelect";
import AgentListSelect from "../shared/AgentListSelect";
import DepartmentList from "../shared/DepartmentListSelect";
import DatesRangePicker from "../shared/DatesRangePicker";
import OpenGroupItemQuickAdd from "./builder/OpenGroupItemQuickAdd";
import OpenGroupItemsList from "./builder/OpenGroupItemsList";
import OpenGroupBuilderDayByDay from "./builder/OpenGroupBuilderDayByDay";
import OpenGroupDeparturesTab from "./builder/OpenGroupDeparturesTab";
import ModalSupplierEdit from "../shared/ModalSupplierEdit.vue";
import BrandListSelect from "../shared/BrandListSelect";
import GtMultiSelect from "../shared/GtMultiSelect";
import RemarksEditDialog from "../itineraries/builder/tools/RemarksEditDialog";
import ServiceStatusListSelect from "../shared/ServiceStatusListSelect";
import ItineraryStatusListSelect from "../shared/ItineraryStatusListSelect";
import { removeGQLTypename } from "../../../helpers";

const OPEN_GROUP_QUERY = gql`
  query openGroup($id: ID!) {
    openGroup(id: $id) {
      id
      name
      description
      department
      duration
      adults
      children
      validFrom
      validTo
      departureDays
      brand
      serviceStatusId
      internalStatus
      destination {
        id
        name
      }
      agent {
        id
        name
      }
      items {
        edges {
          node {
            id
            uid
            parentUid
            day
            order
            duration
            ratePlan
            includeMarkup
            hidden
            optional
            included
            qty
            adults
            children
            service {
              id
              name
              stdOccupancy
            }
            supplier {
              id
              name
              serviceType
            }
            extras {
              id
              extraId
              name
              day
              duration
              included
              optional
            }
            remarks {
              agentNotes
              supplierNotes
              voucherNotes
              internalNotes
              pickupPoint
              pickupTime
              dropoffPoint
              dropoffTime
              roomNumbers
              timeFrom
              flightNo
              confirmationNo
            }
          }
        }
      }
    }
  }
`;

const CREATE_OPEN_GROUP = gql`
  mutation createOpenGroup($input: OpenGroupInput!) {
    createOpenGroup(openGroupInput: $input) {
      id
    }
  }
`;

const UPDATE_OPEN_GROUP = gql`
  mutation updateOpenGroup($id: ID!, $input: OpenGroupInput!) {
    updateOpenGroup(id: $id, openGroupInput: $input) {
      id
    }
  }
`;

export default {
  name: "OpenGroupBuilder",

  components: {
    DestinationListSelect,
    AgentListSelect,
    DepartmentList,
    DatesRangePicker,
    OpenGroupItemQuickAdd,
    OpenGroupItemsList,
    OpenGroupBuilderDayByDay,
    OpenGroupDeparturesTab,
    ModalSupplierEdit,
    BrandListSelect,
    GtMultiSelect,
    RemarksEditDialog,
    ServiceStatusListSelect,
    ItineraryStatusListSelect,
  },

  data() {
    return {
      debug: false,
      loaded: false,
      showSupplierEditor: false,
      editSupplier: null,
      remarksDialog: false,
      remarksItem: null,
      remarksValue: null,
      dayByDay: this.$store.getters.localSettings(
        "open-group-builder-dnd",
        true
      ),
      step: 1,
      items: [],
      name: "",
      brand: "",
      description: "",
      department: "",
      duration: null,
      adults: 0,
      children: 0,
      destination: null,
      agentId: null,
      validFrom: null,
      validTo: null,
      departureDays: [],
      serviceStatusId: "",
      internalStatus: "",
      dayOptions: [
        { name: "Monday", value: "1" },
        { name: "Tuesday", value: "2" },
        { name: "Wednesday", value: "3" },
        { name: "Thursday", value: "4" },
        { name: "Friday", value: "5" },
        { name: "Saturday", value: "6" },
        { name: "Sunday", value: "0" },
      ],
    };
  },

  computed: {
    id() {
      return this.$route.params.id;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    isValid() {
      return this.name != "";
    },
    editLabel() {
      return `${this.name}${
        this.destination ? " / " + this.destination.name : ""
      }${this.duration ? " / " + this.duration + " days" : ""}`;
    },
    saveLabel() {
      return this.id ? "Update Open Group" : "Create Open Group";
    },
  },

  methods: {
    onDayByDayViewChange() {
      this.$store.dispatch("updateLocalSettings", {
        "open-group-builder-dnd": this.dayByDay,
      });
    },
    onUpdateItem(item) {
      const index = this.items.findIndex((i) => i.uid == item.uid);
      if (index !== -1) {
        this.$set(this.items, index, item);
      }
    },
    onExtraRemoveDnd({ extra, item }) {
      const editedItem = this.items.find((i) => i.uid == item.uid);
      if (editedItem) {
        if (extra.id) {
          editedItem.extras = editedItem.extras.filter((e) => e.id != extra.id);
        } else {
          editedItem.extras = editedItem.extras.filter((e) => e.extraId != extra.extraId);
        }
      }
    },
    onDestinationSelect(dest) {
      this.destination = dest;
    },
    onRangeSelected({ dateFrom, dateTo }) {
      this.validFrom = dateFrom;
      this.validTo = dateTo;
    },
    onAddItem(payload) {
      const needsPaxDefaults = payload.qty == null;
      const item = {
        ...payload,
        qty: needsPaxDefaults
          ? (payload.stdOccupancy ? Math.ceil((Number(this.adults) + Number(this.children)) / Number(payload.stdOccupancy)) || 1 : 1)
          : payload.qty,
        adults: needsPaxDefaults ? this.adults : payload.adults,
        children: needsPaxDefaults ? this.children : payload.children,
        order:
          Number(payload.startDay) * 100 +
          (this.items.filter(
            (i) => i.startDay == payload.startDay && i.uid != payload.uid
          ).length +
            1) *
            5,
      };
      if (item.parentUid && item.parentUid != "") {
        const parent = this.items.find((i) => i.uid == item.parentUid);
        if (parent) {
          item.linkParent = `${parent.supplierName} - ${parent.serviceName}`;
        }
      }
      this.items.push(item);
    },
    onRemoveItem(item) {
      this.items = this.items.filter((i) => i.uid != item.uid);
    },
    onOpenItemRemarks(item) {
      this.remarksItem = item;
      this.remarksValue = {
        remarks: item.remarks ? { ...item.remarks } : {},
        serviceType: item.serviceType,
        serviceName: item.serviceName,
        supplierName: item.supplierName,
        rateTypeCode: item.ratePlan || "",
        qty: item.qty != null ? item.qty : 1,
        adults: item.adults != null ? item.adults : 0,
        children: item.children != null ? item.children : 0,
        infants: 0,
      };
      this.remarksDialog = true;
    },
    onSaveItemRemarks(remarks) {
      if (this.remarksItem) {
        this.$set(this.remarksItem, "remarks", { ...remarks });
      }
      this.remarksDialog = false;
    },
    toItemInput(item) {
      return {
        serviceId: item.serviceId,
        uid: item.uid,
        parentUid: item.parentUid,
        day: Number(item.startDay),
        order: Number(item.order),
        duration: Number(item.duration),
        ratePlan: item.ratePlan,
        includeMarkup: item.includeMarkup,
        hidden: item.hidden,
        optional: item.optional,
        included: item.included,
        qty: Number(item.qty),
        adults: Number(item.adults),
        children: Number(item.children),
        remarks: item.remarks ? this.toRemarksInput(item.remarks) : undefined,
        extras:
          item.extras && item.extras.length > 0
            ? item.extras.map((e) => ({
                extraId: e.extraId,
                name: e.name,
                day: Number(e.startDay || e.day),
                duration: Number(e.duration),
                included: e.included,
                optional: e.optional,
              }))
            : [],
      };
    },
    toRemarksInput(remarks) {
      const r = removeGQLTypename(remarks);
      return {
        agentNotes: r.agentNotes,
        supplierNotes: r.supplierNotes,
        voucherNotes: r.voucherNotes,
        internalNotes: r.internalNotes,
        pickupPoint: r.pickupPoint,
        pickupTime: r.pickupTime,
        dropoffPoint: r.dropoffPoint,
        dropoffTime: r.dropoffTime,
        roomNumbers: r.roomNumbers,
        timeFrom: r.timeFrom,
        flightNo: r.flightNo,
        confirmationNo: r.confirmationNo,
      };
    },
    fromItem(item) {
      return {
        id: item.id,
        uid: item.uid,
        parentUid: item.parentUid,
        serviceType: item.supplier ? item.supplier.serviceType : "",
        serviceName: item.service ? item.service.name : "",
        serviceId: item.service ? item.service.id : null,
        stdOccupancy: item.service ? item.service.stdOccupancy : null,
        supplierId: item.supplier ? item.supplier.id : null,
        supplierName: item.supplier ? item.supplier.name : "",
        startDay: item.day,
        order: item.order,
        duration: item.duration,
        ratePlan: item.ratePlan || "",
        includeMarkup: item.includeMarkup || false,
        hidden: item.hidden || false,
        optional: item.optional || false,
        included: item.included !== false,
        qty: item.qty != null ? item.qty : 1,
        adults: item.adults != null ? item.adults : this.adults,
        children: item.children != null ? item.children : this.children,
        remarks: item.remarks ? removeGQLTypename(item.remarks) : {},
        extras:
          item.extras && item.extras.length > 0
            ? item.extras.map((e) => ({
                id: e.id,
                extraId: e.extraId,
                name: e.name,
                startDay: e.day,
                duration: e.duration,
                included: e.included,
                optional: e.optional,
              }))
            : [],
      };
    },
    buildInput() {
      return {
        name: this.name,
        brand: this.brand,
        description: this.description,
        department: this.department,
        duration: this.duration,
        adults: this.adults,
        children: this.children,
        destinationId: this.destination ? this.destination.id : null,
        agentId: this.agentId,
        validFrom: this.validFrom,
        validTo: this.validTo,
        departureDays: this.departureDays.join(","),
        serviceStatusId: this.serviceStatusId || null,
        internalStatus: this.internalStatus || null,
        items: this.items.map(this.toItemInput),
      };
    },
    onSave() {
      if (this.id) {
        this.$apollo
          .mutate({
            mutation: UPDATE_OPEN_GROUP,
            variables: { id: this.id, input: this.buildInput() },
          })
          .then(() => {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Open Group saved.",
            });
            //this.$router.push("/open-groups");
          })
          .catch((err) => {
            this.$store.dispatch("showAppSnackbar", {
              msg: err.message,
              color: "error",
            });
          });
      } else {
        this.$apollo
          .mutate({
            mutation: CREATE_OPEN_GROUP,
            variables: { input: this.buildInput() },
          })
          .then((response) => {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Open Group created.",
            });
            this.$router.replace({
              path: `/open-group/${response.data.createOpenGroup.id}/edit`,
            });
          })
          .catch((err) => {
            this.$store.dispatch("showAppSnackbar", {
              msg: err.message,
              color: "error",
            });
          });
      }
    },
    onSupplierEdit(payload) {
      this.editSupplier = {
        ...payload,
        supplierName: payload.hasOwnProperty("supplierName")
          ? payload.supplierName
          : payload.name,
        supplierId: payload.hasOwnProperty("supplierId")
          ? payload.supplierId
          : payload.id,
      };
      this.showSupplierEditor = true;
    },
    loadOpenGroup(data) {
      const og = data.openGroup;
      if (!og) return;
      this.name = og.name || "";
      this.brand = og.brand || "";
      this.description = og.description || "";
      this.department = og.department || "";
      this.duration = og.duration || null;
      this.adults = og.adults || 0;
      this.children = og.children || 0;
      this.destination = og.destination || null;
      this.agentId = og.agent ? og.agent.id : null;
      this.validFrom = og.validFrom ? og.validFrom.substring(0, 10) : null;
      this.validTo = og.validTo ? og.validTo.substring(0, 10) : null;
      this.departureDays = og.departureDays ? og.departureDays.split(",").filter(Boolean) : [];
      this.serviceStatusId = og.serviceStatusId || "";
      this.internalStatus = og.internalStatus || "";
      this.loaded = true;
      this.items =
        og.items && og.items.edges
          ? og.items.edges.map((e) => this.fromItem(e.node))
          : [];
    },
  },

  apollo: {
    openGroup: {
      query: OPEN_GROUP_QUERY,
      variables() {
        return { id: this.id };
      },
      skip() {
        return !this.id;
      },
      fetchPolicy: "network-only",
      result({ data }) {
        if (!this.loaded) this.loadOpenGroup(data);
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/open-groups/OpenGroupBuilder.vue